import { saveAs } from 'file-saver';
import JSZip from 'jszip';

/**
 * AO-03 "Export to Word" — browser-side, frontend-only.
 *
 * Implementation: markdown → Word XML (OOXML) → .docx (ZIP) via jszip.
 * No Node.js dependencies; bundles cleanly under CRA / react-scripts 5.
 *
 * Formatting support: headings (#–######), bullet lists, numbered lists,
 * GFM tables, blockquotes, code blocks/inline code, bold, italic.
 * Complex/nested content and KaTeX math are rendered best-effort as plain text.
 */

// ---------------------------------------------------------------------------
// Helpers: XML escaping & inline markdown → OOXML runs
// ---------------------------------------------------------------------------

const xmlEsc = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

type Run = { text: string; bold?: boolean; italic?: boolean; mono?: boolean };

/** Parse inline markdown (bold, italic, code) into Run objects. */
const parseInline = (text: string): Run[] => {
  const runs: Run[] = [];
  const tokenRegex = /(\*\*[^*]+\*\*|__[^_]+__|\*[^*]+\*|_[^_]+_|`[^`]+`)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = tokenRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      runs.push({ text: text.slice(lastIndex, match.index) });
    }
    const token = match[0];
    if (token.startsWith('**') || token.startsWith('__')) {
      runs.push({ text: token.slice(2, -2), bold: true });
    } else if (token.startsWith('`')) {
      runs.push({ text: token.slice(1, -1), mono: true });
    } else {
      runs.push({ text: token.slice(1, -1), italic: true });
    }
    lastIndex = tokenRegex.lastIndex;
  }
  if (lastIndex < text.length) runs.push({ text: text.slice(lastIndex) });
  if (runs.length === 0) runs.push({ text: '' });
  return runs;
};

/** Convert Run[] → <w:r> XML string. */
const runsToXml = (runs: Run[]): string =>
  runs
    .map((r) => {
      const rpr = [
        r.bold ? '<w:b/>' : '',
        r.italic ? '<w:i/>' : '',
        r.mono ? '<w:rFonts w:ascii="Courier New" w:hAnsi="Courier New"/>' : '',
      ]
        .filter(Boolean)
        .join('');
      const rprXml = rpr ? `<w:rPr>${rpr}</w:rPr>` : '';
      return `<w:r>${rprXml}<w:t xml:space="preserve">${xmlEsc(r.text)}</w:t></w:r>`;
    })
    .join('');

// ---------------------------------------------------------------------------
// Block builders — each returns a <w:p> or <w:tbl> XML string
// ---------------------------------------------------------------------------

/** Heading styles map: # level → Word style name */
const HEADING_STYLE: Record<number, string> = {
  1: 'Heading1',
  2: 'Heading2',
  3: 'Heading3',
  4: 'Heading4',
  5: 'Heading5',
  6: 'Heading6',
};

const pHeading = (level: number, text: string): string => {
  const style = HEADING_STYLE[level] || 'Heading1';
  return `<w:p><w:pPr><w:pStyle w:val="${style}"/></w:pPr>${runsToXml(parseInline(text))}</w:p>`;
};

const pTitle = (text: string): string =>
  `<w:p><w:pPr><w:pStyle w:val="Title"/></w:pPr>${runsToXml(parseInline(text))}</w:p>`;

const pBullet = (text: string, level: number): string =>
  `<w:p><w:pPr><w:numPr><w:ilvl w:val="${level}"/><w:numId w:val="1"/></w:numPr></w:pPr>${runsToXml(
    parseInline(text)
  )}</w:p>`;

const pNumbered = (text: string, level: number): string =>
  `<w:p><w:pPr><w:numPr><w:ilvl w:val="${level}"/><w:numId w:val="2"/></w:numPr></w:pPr>${runsToXml(
    parseInline(text)
  )}</w:p>`;

const pNormal = (text: string): string => `<w:p>${runsToXml(parseInline(text))}</w:p>`;

const pMono = (text: string): string =>
  `<w:p><w:r><w:rPr><w:rFonts w:ascii="Courier New" w:hAnsi="Courier New"/></w:rPr><w:t xml:space="preserve">${xmlEsc(
    text
  )}</w:t></w:r></w:p>`;

const pIndented = (text: string): string =>
  `<w:p><w:pPr><w:ind w:left="720"/></w:pPr>${runsToXml(parseInline(text))}</w:p>`;

const pEmpty = (): string => `<w:p/>`;

// ---------------------------------------------------------------------------
// Table builder
// ---------------------------------------------------------------------------

const isTableRow = (line: string): boolean => line.trim().startsWith('|') && line.includes('|');
const isTableSep = (line: string): boolean =>
  /^\s*\|?[\s:|-]+\|?\s*$/.test(line) && line.includes('-');

const splitCells = (line: string): string[] => {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split('|').map((c) => c.trim());
};

const buildTable = (rows: string[]): string => {
  const parsed = rows.map(splitCells);
  const cols = Math.max(...parsed.map((r) => r.length), 1);
  const W = Math.floor(9216 / cols); // ~6 inch table, equal columns (twips)

  const tblRows = parsed
    .map((cells, ri) => {
      const isHeader = ri === 0;
      const tcs = Array.from({ length: cols }, (_, ci) => {
        const cell = cells[ci] ?? '';
        const runs = isHeader
          ? runsToXml([{ text: cell, bold: true }])
          : runsToXml(parseInline(cell));
        return `<w:tc><w:tcPr><w:tcW w:w="${W}" w:type="dxa"/></w:tcPr><w:p>${runs}</w:p></w:tc>`;
      }).join('');
      return `<w:tr>${isHeader ? '<w:trPr><w:tblHeader/></w:trPr>' : ''}${tcs}</w:tr>`;
    })
    .join('');

  return `<w:tbl><w:tblPr><w:tblStyle w:val="TableGrid"/><w:tblW w:w="9216" w:type="dxa"/></w:tblPr><w:tblGrid>${Array.from(
    { length: cols },
    () => `<w:gridCol w:w="${W}"/>`
  ).join('')}</w:tblGrid>${tblRows}</w:tbl>`;
};

// ---------------------------------------------------------------------------
// Main markdown → XML blocks converter
// ---------------------------------------------------------------------------

const buildBlocks = (markdown: string): string => {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const parts: string[] = [];
  let inCodeBlock = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    if (trimmed.startsWith('```')) {
      inCodeBlock = !inCodeBlock;
      continue;
    }
    if (inCodeBlock) {
      parts.push(pMono(line));
      continue;
    }
    if (trimmed === '') {
      parts.push(pEmpty());
      continue;
    }

    // Table: collect consecutive table lines
    if (isTableRow(line)) {
      const tableLines: string[] = [];
      while (i < lines.length && isTableRow(lines[i])) {
        if (!isTableSep(lines[i])) tableLines.push(lines[i]);
        i++;
      }
      i--; // outer loop increments
      if (tableLines.length > 0) parts.push(buildTable(tableLines));
      continue;
    }

    // Heading
    const hm = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (hm) {
      parts.push(pHeading(hm[1].length, hm[2]));
      continue;
    }

    // Blockquote
    const qm = trimmed.match(/^>\s?(.*)$/);
    if (qm) {
      parts.push(pIndented(qm[1]));
      continue;
    }

    // Numbered list
    const om = line.match(/^(\s*)(\d+)[.)]\s+(.*)$/);
    if (om) {
      const lvl = Math.min(Math.floor(om[1].length / 2), 2);
      parts.push(pNumbered(om[3], lvl));
      continue;
    }

    // Bullet list
    const bm = line.match(/^(\s*)[*+-]\s+(.*)$/);
    if (bm) {
      const lvl = Math.min(Math.floor(bm[1].length / 2), 2);
      parts.push(pBullet(bm[2], lvl));
      continue;
    }

    // Normal paragraph
    parts.push(pNormal(trimmed));
  }

  return parts.join('\n');
};

// ---------------------------------------------------------------------------
// OOXML document assembly
// ---------------------------------------------------------------------------

/**
 * Minimal numbering definitions:
 *   numId 1 = bullet list (•)
 *   numId 2 = numbered list (1., 2., …)
 */
const NUMBERING_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:numbering xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:abstractNum w:abstractNumId="0">
    <w:multiLevelType w:val="hybridMultilevel"/>
    ${[0, 1, 2]
      .map(
        (lvl) => `<w:lvl w:ilvl="${lvl}">
      <w:start w:val="1"/><w:numFmt w:val="bullet"/>
      <w:lvlText w:val="•"/>
      <w:lvlJc w:val="left"/>
      <w:pPr><w:ind w:left="${720 + lvl * 360}" w:hanging="360"/></w:pPr>
    </w:lvl>`
      )
      .join('\n')}
  </w:abstractNum>
  <w:abstractNum w:abstractNumId="1">
    <w:multiLevelType w:val="hybridMultilevel"/>
    ${[0, 1, 2]
      .map(
        (lvl) => `<w:lvl w:ilvl="${lvl}">
      <w:start w:val="1"/><w:numFmt w:val="decimal"/>
      <w:lvlText w:val="%${lvl + 1}."/>
      <w:lvlJc w:val="left"/>
      <w:pPr><w:ind w:left="${720 + lvl * 360}" w:hanging="360"/></w:pPr>
    </w:lvl>`
      )
      .join('\n')}
  </w:abstractNum>
  <w:num w:numId="1"><w:abstractNumId w:val="0"/></w:num>
  <w:num w:numId="2"><w:abstractNumId w:val="1"/></w:num>
</w:numbering>`;

const CONTENT_TYPES_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>
  <Override PartName="/word/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.styles+xml"/>
  <Override PartName="/word/numbering.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.numbering+xml"/>
  <Override PartName="/word/settings.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.settings+xml"/>
</Types>`;

const RELS_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
</Relationships>`;

const WORD_RELS_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/numbering" Target="numbering.xml"/>
  <Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/settings" Target="settings.xml"/>
</Relationships>`;

const SETTINGS_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:settings xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:defaultTabStop w:val="720"/>
</w:settings>`;

/** Minimal styles: Normal, Title, Heading1–6, TableGrid */
const STYLES_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:styles xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"
          xmlns:w14="http://schemas.microsoft.com/office/word/2010/wordml">
  <w:style w:type="paragraph" w:default="1" w:styleId="Normal">
    <w:name w:val="Normal"/>
    <w:rPr><w:sz w:val="24"/><w:szCs w:val="24"/></w:rPr>
  </w:style>
  <w:style w:type="paragraph" w:styleId="Title">
    <w:name w:val="Title"/>
    <w:basedOn w:val="Normal"/>
    <w:pPr><w:spacing w:after="240"/></w:pPr>
    <w:rPr><w:b/><w:sz w:val="52"/><w:szCs w:val="52"/></w:rPr>
  </w:style>
  ${[1, 2, 3, 4, 5, 6]
    .map((n) => {
      const sz = [40, 36, 32, 28, 26, 24][n - 1];
      return `<w:style w:type="paragraph" w:styleId="Heading${n}">
    <w:name w:val="heading ${n}"/>
    <w:basedOn w:val="Normal"/>
    <w:pPr><w:spacing w:before="${n <= 2 ? 240 : 120}" w:after="120"/></w:pPr>
    <w:rPr><w:b/><w:sz w:val="${sz}"/><w:szCs w:val="${sz}"/></w:rPr>
  </w:style>`;
    })
    .join('\n  ')}
  <w:style w:type="table" w:styleId="TableGrid">
    <w:name w:val="Table Grid"/>
    <w:tblPr><w:tblBorders>
      <w:top w:val="single" w:sz="4" w:space="0" w:color="auto"/>
      <w:left w:val="single" w:sz="4" w:space="0" w:color="auto"/>
      <w:bottom w:val="single" w:sz="4" w:space="0" w:color="auto"/>
      <w:right w:val="single" w:sz="4" w:space="0" w:color="auto"/>
      <w:insideH w:val="single" w:sz="4" w:space="0" w:color="auto"/>
      <w:insideV w:val="single" w:sz="4" w:space="0" w:color="auto"/>
    </w:tblBorders></w:tblPr>
  </w:style>
</w:styles>`;

const buildDocumentXml = (bodyContent: string): string =>
  `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"
            xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <w:body>
    ${bodyContent}
    <w:sectPr>
      <w:pgSz w:w="12240" w:h="15840"/>
      <w:pgMar w:top="1440" w:right="1440" w:bottom="1440" w:left="1440"/>
    </w:sectPr>
  </w:body>
</w:document>`;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

const sanitizeFileName = (name: string): string => {
  const cleaned = name
    .replace(/[\\/:*?"<>|]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80);
  return cleaned || 'amatrium-answer';
};

export interface ExportToWordOptions {
  /** The answer content (markdown/plain text). */
  content: string;
  /** Optional title rendered at the top of the document (e.g. the question). */
  title?: string;
  /** File name without extension; sanitized automatically. */
  fileName?: string;
}

/**
 * Build a .docx from an already-rendered answer and trigger a browser download.
 * Uses jszip to assemble OOXML — no Node.js APIs, bundles cleanly under CRA.
 */
export const exportAnswerToWord = async ({
  content,
  title,
  fileName,
}: ExportToWordOptions): Promise<void> => {
  const bodyParts: string[] = [];

  if (title && title.trim()) {
    bodyParts.push(pTitle(title.trim()));
    bodyParts.push(pEmpty());
  }

  bodyParts.push(buildBlocks(content ?? ''));

  const documentXml = buildDocumentXml(bodyParts.join('\n'));

  const zip = new JSZip();
  zip.file('[Content_Types].xml', CONTENT_TYPES_XML);
  zip.file('_rels/.rels', RELS_XML);
  zip.file('word/document.xml', documentXml);
  zip.file('word/styles.xml', STYLES_XML);
  zip.file('word/numbering.xml', NUMBERING_XML);
  zip.file('word/settings.xml', SETTINGS_XML);
  zip.file('word/_rels/document.xml.rels', WORD_RELS_XML);

  const blob = await zip.generateAsync({
    type: 'blob',
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
  saveAs(blob, `${sanitizeFileName(fileName || title || 'amatrium-answer')}.docx`);
};
